import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { AlertController } from "@ionic/angular";
import * as _ from "lodash";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { distinctUntilChanged } from "rxjs/operators";
import * as fromApp from "../../../../../../store/app.reducer";
import * as selectors from "../../../../../../store/app.selectors";
import { Subscription } from "rxjs";

@Component({
  selector: "app-quantity-only-image-checkbox-choice",
  templateUrl: "./quantity-only-image-checkbox-choice.component.html",
  styleUrls: [
    "../no-quantity-only-image-checkbox-choice/no-quantity-only-image-checkbox-choice.component.scss",
  ],
})
export class QuantityOnlyImageCheckboxChoiceComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() selectedProductId: any;
  @Input() bundleMode: any;
  @Input() option_id;
  @Input() choice_id;
  @Input() selectedProductPromo: any;
  @Input() selectedProductCategory: any;
  @Input() showChoicePrices: boolean;
  @Input() imagePath;
  @Input() alt_dinein_active: any;
  @Input() groupStoreData: any;
  @Output() choiceChanged = new EventEmitter();

  public option: any;
  public choice;
  public orderPickup;
  public dineIn;
  public selectedDineInTable;
  public isPulsing = false;
  public showQuantityComponent: boolean;

  private subscription: Subscription;
  private optionSubscription: Subscription;

  constructor(
    private alertCtrl: AlertController,
    private translateService: TranslateService,
    private store: Store<fromApp.AppState>,
    private changeDetector: ChangeDetectorRef
  ) {}

  selectOption() {
    if (this.optionSubscription) {
      this.optionSubscription.unsubscribe();
    }
    this.optionSubscription = this.store
      .select(
        selectors.getOptionByOptionId(this.selectedProductId, this.option_id)
      )
      .pipe(distinctUntilChanged())
      .subscribe((state) => {
        if (!_.isEqual(this.option, state)) {
          this.option = _.cloneDeep(state);
          if (
            this.option &&
            this.option.freeChoices &&
            this.option.freeChoices !== "0"
          ) {
            if (this.option.optionData && this.option.optionData.freeChoices) {
              this.showChoicePrices = true;
            } else {
              this.showChoicePrices = false;
            }
          } else {
            this.showChoicePrices = true;
          }
          if (this.option && this.option.choices) {
            let choiceIndex = _.findIndex(this.option.choices, {
              choice_id: this.choice_id,
            });
            if (
              choiceIndex !== -1 &&
              !_.isEqual(this.choice, this.option.choices[choiceIndex])
            ) {
              this.choice = this.option.choices[choiceIndex];
              this.showQuantityElementDebounce();
            }
          }
        }
      });
  }

  ngOnChanges(simpleChange: SimpleChanges) {
    if (
      simpleChange &&
      simpleChange.option_id &&
      simpleChange.option_id.currentValue !==
        simpleChange.option_id.previousValue
    ) {
      this.selectOption();
    }
  }

  ngOnInit() {
    this.selectOption();
    this.subscription = this.store
      .select("deliveryMethodAddress")
      .pipe(distinctUntilChanged())
      .subscribe((state) => {
        if (state && !_.isEqual(this.dineIn, state.dineIn)) {
          this.dineIn = _.cloneDeep(state.dineIn);
        }
        if (state && !_.isEqual(this.orderPickup, state.orderPickup)) {
          this.orderPickup = _.cloneDeep(state.orderPickup);
        }
        if (
          state &&
          !_.isEqual(state.selectedDineInTable, this.selectedDineInTable)
        ) {
          this.selectedDineInTable = _.cloneDeep(state.selectedDineInTable);
        }
      });
  }

  showQuantityElementDebounce = _.debounce(() => {
    this.showQuantityComponent = true;
  }, 120);

  imagePulseOnSelect() {
    this.isPulsing = true;
    setTimeout(() => {
      this.isPulsing = false;
    }, 1000);
    this.changeDetector.detectChanges();
  }

  selectChoice() {
    if (!this.choice) {
      return;
    }
    this.choice.is_preselected = !this.choice.is_preselected;
    if (
      this.option &&
      this.option.freeChoices &&
      this.option.freeChoicesBySelectionOrder
    ) {
      if (this.choice.is_preselected) {
        this.choice.selected_timestamp = Date.now().toString();
      } else {
        delete this.choice.selected_timestamp;
      }
    }
    if (this.choice.is_preselected) {
      this.imagePulseOnSelect();
    }
    this.choiceChanged.emit(this.choice);
  }

  changeChoiceQuantity(ev) {
    if (ev === "increase") {
      this.choiceQuantityIncrease();
    } else if (ev === "decrease") {
      this.choiceQuantityDecrease();
    }
  }

  choiceQuantityIncrease() {
    let qtyStep = this.choice.quantityInChoicesStep || 1;
    if (
      this.choice.is_preselected &&
      this.choice.activeTotal &&
      this.choice.availableTotal
    ) {
      if (this.checkMaximumChoices()) {
        this.choice.quantity = this.choice.quantity + qtyStep;
        this.choiceChanged.emit(this.choice);
      } else {
        this.presentAlert(
          this.translateService.instant(
            "product-details.options.checkbox-options.maximum-choices-allowed"
          )
        );
      }
    }
  }

  choiceQuantityDecrease() {
    var qtyStep = this.choice.quantityInChoicesStep || 1;
    if (
      this.choice.quantity > qtyStep &&
      this.choice.is_preselected &&
      this.choice.activeTotal &&
      this.choice.availableTotal
    ) {
      this.choice.quantity = this.choice.quantity - qtyStep;
      this.choiceChanged.emit(this.choice);
    }
  }

  checkMaximumChoices() {
    var optionsMaxChoices = parseInt(this.option.maxChoices);
    var counter = 0;

    if (!_.isNaN(optionsMaxChoices) && optionsMaxChoices > 0) {
      _.each(_.sortBy(this.option.choices, "price"), (choice) => {
        if (
          choice.is_preselected &&
          choice.activeTotal &&
          choice.availableTotal
        ) {
          counter = counter + choice.quantity;
        }
      });
      if (counter >= optionsMaxChoices) {
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  }

  async presentAlert(message) {
    const alert = await this.alertCtrl.create({
      message: message,
      buttons: ["OK"],
    });

    await alert.present();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.optionSubscription) {
      this.optionSubscription.unsubscribe();
    }
  }
}
